import { useCallback, useEffect, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

// Vocabulary list + CRUD for the /english module.
//
// Reads the full vocab list for the current user (RLS limits to own
// rows). Mutations go through the english-vocab-enrich edge function
// for inserts (so the LLM enrichment + AR translation runs server-
// side); direct Supabase calls handle edits and deletes.
//
// Filtering happens client-side because the typical learner has at
// most a few hundred vocab rows — a single fetch is cheaper than the
// network round-trip per keystroke that server-side search would
// cost, and we avoid building an FTS index for such a small dataset.
export function useEnglishVocab({ search = '', state = 'all' } = {}) {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setError(null)
    const { data, error: err } = await supabase
      .from('english_vocab')
      .select('*')
      .order('created_at', { ascending: false })
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    setRows(data || [])
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  // Add a new word via the enrich edge function. The function checks
  // for an existing row and short-circuits if found, so calling this
  // for a word the user already has is safe (returns the cached row
  // and we surface it in the list).
  const add = useCallback(async ({ word, context_sentence, context_source_url, context_source_kind } = {}) => {
    if (!word) return { error: 'word required' }
    const { data, error: err } = await supabase.functions.invoke('english-vocab-enrich', {
      body: { word, context_sentence, context_source_url, context_source_kind },
    })
    if (err) return { error: err.message || 'save failed' }
    if (data?.error) return { error: data.error }
    if (data?.vocab) {
      // Optimistic insert into local state; on next fetch the row is
      // confirmed. Pre-pending matches the created_at DESC order.
      setRows((prev) => {
        const without = prev.filter((r) => r.id !== data.vocab.id)
        return [data.vocab, ...without]
      })
      return { vocab: data.vocab, source: data.source }
    }
    return { error: 'empty response' }
  }, [])

  const remove = useCallback(async (id) => {
    if (!id) return { error: 'id required' }
    // Optimistic — drop locally, rollback on error.
    const snapshot = rows
    setRows((prev) => prev.filter((r) => r.id !== id))
    const { error: err } = await supabase.from('english_vocab').delete().eq('id', id)
    if (err) {
      setRows(snapshot)
      return { error: err.message }
    }
    return { ok: true }
  }, [rows])

  const update = useCallback(async (id, patch) => {
    if (!id) return { error: 'id required' }
    const { data, error: err } = await supabase
      .from('english_vocab')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select('*')
      .single()
    if (err) return { error: err.message }
    setRows((prev) => prev.map((r) => (r.id === id ? data : r)))
    return { vocab: data }
  }, [])

  // Client-side filter: case-insensitive substring across word +
  // definition_en + definition_ar, plus an SRS state filter chip.
  const filtered = applyFilters(rows, { search, state })

  return {
    rows,
    filtered,
    loading,
    error,
    refetch: fetch,
    add,
    remove,
    update,
  }
}

function applyFilters(rows, { search, state }) {
  const q = (search || '').trim().toLowerCase()
  let out = rows
  if (state && state !== 'all') {
    out = out.filter((r) => r.srs_state === state)
  }
  if (q) {
    out = out.filter((r) => (
      (r.word || '').toLowerCase().includes(q)
      || (r.definition_en || '').toLowerCase().includes(q)
      || (r.definition_ar || '').toLowerCase().includes(q)
    ))
  }
  return out
}
